import { useState, useEffect, useId, useMemo, useCallback, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type GitProvider, type RemoteRepository, type GitHubAppStatusResponse } from '../api';
import { getSavedGitCredential, saveGitCredential, removeGitCredential, type SavedGitCredential } from '../utils/gitCredentials';
import { ConfirmDialog, ErrorNotice, Field, LoadingRows, StatusBadge } from '../components/ui';
import { ActionMenu, Callout, Dialog, Icon, Input, RowActions, Section, Subsection, Table, Tabs, Textarea } from '../components/primitives';
import { ResponsiveTable } from '../components/primitives/ResponsiveTable';
import type { Column } from '../components/primitives/ResponsiveTable';
import { BitbucketIcon, GithubIcon, GitlabIcon } from '../components/repository/icons';

/**
 * Settings › Repository access (CO-91, CO-160): one row per Git provider —
 * provider, connected account, status, scope and actions. Setting a provider
 * up opens only from its Connect action, and how credentials are kept is
 * said once, behind help. The only primary action on the view is the step
 * that sets GitHub up; every other action is secondary.
 */

type TokenProvider = Exclude<GitProvider, 'github'>;

interface ProviderMeta {
  id: TokenProvider;
  name: string;
  defaultHost: string;
  tokenType: string;
  tokenPlaceholder: string;
  tokenDocUrl: string;
  scopesNote: string;
}

const PROVIDERS: ProviderMeta[] = [
  {
    id: 'gitlab',
    name: 'GitLab',
    defaultHost: 'gitlab.com',
    tokenType: 'Personal access token',
    tokenPlaceholder: 'glpat-…',
    tokenDocUrl: 'https://gitlab.com/-/profile/personal_access_tokens',
    scopesNote: 'Needs the read_api and read_repository scopes.',
  },
  {
    id: 'bitbucket',
    name: 'Bitbucket',
    defaultHost: 'bitbucket.org',
    tokenType: 'App password',
    tokenPlaceholder: 'App password…',
    tokenDocUrl: 'https://bitbucket.org/account/settings/app-passwords/',
    scopesNote: 'Needs the repository:read and account:read permissions.',
  },
];

function providerLogo(id: GitProvider, size: number) {
  if (id === 'gitlab') return <GitlabIcon size={size} />;
  if (id === 'bitbucket') return <BitbucketIcon size={size} />;
  return <GithubIcon size={size} />;
}

/** A provider name with its logo, for section and dialog titles. */
function ProviderTitle({ provider, children }: { provider: GitProvider; children: ReactNode }) {
  return (
    <span className="integrations-provider">
      {providerLogo(provider, 20)}
      {children}
    </span>
  );
}

function asError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error(String(cause));
}

function useFilteredRepos(repos: RemoteRepository[], search: string) {
  return useMemo(() => {
    if (!search.trim()) return repos;
    const term = search.toLowerCase().trim();
    return repos.filter(
      (r) =>
        r.full_name.toLowerCase().includes(term) ||
        r.default_branch?.toLowerCase().includes(term)
    );
  }, [repos, search]);
}

/** The repositories a connection can read, as a Subsection of its provider. */
function RepositoryList({
  id,
  label,
  repos,
  filtered,
  loading,
  error,
  search,
  onSearch,
  onRefresh,
  emptyDescription,
}: {
  id: string;
  label: string;
  repos: RemoteRepository[];
  filtered: RemoteRepository[];
  loading: boolean;
  error: Error | null;
  search: string;
  onSearch: (value: string) => void;
  onRefresh: () => void;
  emptyDescription: string;
}) {
  return (
    <div id={id}>
      <Subsection
        testId="integrations-repositories"
        title={
          <>
            Repositories <span className="badge tabular-nums">{filtered.length}</span>
          </>
        }
        actions={
          <>
            <Input
              type="text"
              className="integrations-filter"
              placeholder="Filter repositories…"
              aria-label="Filter repositories"
              autoComplete="off"
              spellCheck={false}
              value={search}
              onChange={(e) => onSearch(e.target.value)}
            />
            <button
              type="button"
              className="button button--secondary button--small"
              onClick={onRefresh}
              disabled={loading}
            >
              <Icon name={loading ? 'spinner' : 'refresh'} size={16} />
              Refresh
            </button>
          </>
        }
      >
        <ErrorNotice error={error} />
        {loading ? (
          <LoadingRows label="Loading repositories" rows={3} rowHeight="32px" />
        ) : filtered.length === 0 ? (
          <p className="quiet-empty">{repos.length === 0 ? emptyDescription : 'No repository matches'}</p>
        ) : (
          <Table label={label}>
            <thead>
              <tr>
                <th scope="col">Repository</th>
                <th scope="col">Default branch</th>
                <th scope="col">Visibility</th>
                <th scope="col">Magento detection</th>
                <th scope="col" className="integrations-repos__action">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((repo) => (
                <tr key={repo.full_name}>
                  <th scope="row" className="integrations-repos__name">
                    {repo.full_name}
                  </th>
                  <td>
                    <code>{repo.default_branch || 'no default branch'}</code>
                  </td>
                  <td>
                    {repo.private ? (
                      <span className="badge">
                        <Icon name="lock" size={12} /> Private
                      </span>
                    ) : (
                      <span className="badge">
                        <Icon name="globe" size={12} /> Public
                      </span>
                    )}
                  </td>
                  <td>{repo.magento ? <span className="badge">Magento 2</span> : <span className="muted">Standard</span>}</td>
                  <td className="integrations-repos__action">
                    <RowActions>
                      <Link to="/app/projects/new" className="button button--secondary button--small">
                        New project
                      </Link>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Subsection>
    </div>
  );
}

// ── GitHub App setup ──────────────────────────────────────────────────────

type GitHubAppTab = 'automated' | 'manual';

const GITHUB_MODES = [
  { value: 'automated' as const, label: 'Automated installation' },
  { value: 'manual' as const, label: 'Connect existing GitHub App' },
];

/** Setting GitHub up: register an app through GitHub's manifest flow, or connect one that exists. */
function GitHubSetupDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  const [tab, setTab] = useState<GitHubAppTab>('automated');
  const [startingManifest, setStartingManifest] = useState(false);
  const [manifestError, setManifestError] = useState<Error | null>(null);
  const [orgName, setOrgName] = useState('');
  const tabsId = useId();
  const panelId = useId();

  const [manualAppId, setManualAppId] = useState('');
  const [manualAppName, setManualAppName] = useState('');
  const [manualClientId, setManualClientId] = useState('');
  const [manualClientSecret, setManualClientSecret] = useState('');
  const [manualWebhookSecret, setManualWebhookSecret] = useState('');
  const [manualPrivateKey, setManualPrivateKey] = useState('');
  const [manualInstallationId, setManualInstallationId] = useState('');
  const [manualAccount, setManualAccount] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState<Error | null>(null);

  async function handleStartManifestFlow(e: FormEvent) {
    e.preventDefault();
    setStartingManifest(true);
    setManifestError(null);
    try {
      const res = await api.getGitHubAppManifest({ organization_name: orgName.trim() || undefined });
      // GitHub takes the manifest as a posted form.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = res.github_url;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'manifest';
      input.value = JSON.stringify(res.manifest);
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setManifestError(asError(err));
      setStartingManifest(false);
    }
  }

  async function handleManualSave(e: FormEvent) {
    e.preventDefault();
    setManualSaving(true);
    setManualError(null);
    try {
      await api.saveManualGitHubApp({
        app_id: parseInt(manualAppId, 10),
        app_name: manualAppName.trim() || undefined,
        client_id: manualClientId.trim() || undefined,
        client_secret: manualClientSecret.trim(),
        webhook_secret: manualWebhookSecret.trim(),
        private_key: manualPrivateKey.trim(),
        installation_id: parseInt(manualInstallationId, 10),
        account: manualAccount.trim(),
      });
      onConnected();
    } catch (err) {
      setManualError(asError(err));
    } finally {
      setManualSaving(false);
    }
  }

  const manualIncomplete =
    !manualAppId.trim() ||
    !manualInstallationId.trim() ||
    !manualAccount.trim() ||
    !manualClientSecret.trim() ||
    !manualWebhookSecret.trim() ||
    !manualPrivateKey.trim();

  return (
    <Dialog
      open={open}
      size="md"
      title={<ProviderTitle provider="github">Connect GitHub</ProviderTitle>}
      onClose={onClose}
      busy={startingManifest || manualSaving}
      testId="integrations-github-setup"
    >
      <div className="integrations-mode">
        <Tabs label="GitHub connection method" variant="secondary" idBase={tabsId} panelId={panelId} value={tab} onChange={setTab} options={GITHUB_MODES} />
        <div role="tabpanel" id={panelId} aria-labelledby={`${tabsId}-${tab}`}>
          {tab === 'automated' ? (
            <form className="form" onSubmit={handleStartManifestFlow}>
              <Field label="GitHub organization" hint="Optional. Leave it empty to register the app on your personal account.">
                <Input type="text" placeholder="my-org" spellCheck={false} autoComplete="off" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
              </Field>
              <ErrorNotice error={manifestError} />
              <div className="integrations-form__actions">
                <button type="submit" className="button button--primary" disabled={startingManifest}>
                  <GithubIcon size={16} />
                  {startingManifest ? 'Redirecting to GitHub' : 'Register GitHub App'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleManualSave} className="form">
              <div className="form-grid form-grid--2">
                <Field label="App ID">
                  <Input type="text" required placeholder="123456" spellCheck={false} autoComplete="off" value={manualAppId} onChange={(e) => setManualAppId(e.target.value)} disabled={manualSaving} />
                </Field>
                <Field label="App name (optional)">
                  <Input type="text" placeholder="my-commerce-ops-app" spellCheck={false} autoComplete="off" value={manualAppName} onChange={(e) => setManualAppName(e.target.value)} disabled={manualSaving} />
                </Field>
                <Field label="Client ID (optional)">
                  <Input type="text" placeholder="Iv1.abc123…" spellCheck={false} autoComplete="off" value={manualClientId} onChange={(e) => setManualClientId(e.target.value)} disabled={manualSaving} />
                </Field>
                <Field label="Installation ID">
                  <Input type="text" required placeholder="12345678" spellCheck={false} autoComplete="off" value={manualInstallationId} onChange={(e) => setManualInstallationId(e.target.value)} disabled={manualSaving} />
                </Field>
              </div>
              <Field label="Account or organization">
                <Input type="text" required placeholder="my-org or username" spellCheck={false} autoComplete="off" value={manualAccount} onChange={(e) => setManualAccount(e.target.value)} disabled={manualSaving} />
              </Field>
              <Field label="Client secret">
                <Input type="password" required placeholder="Client secret" value={manualClientSecret} onChange={(e) => setManualClientSecret(e.target.value)} disabled={manualSaving} autoComplete="off" />
              </Field>
              <Field label="Webhook secret">
                <Input type="password" required placeholder="Webhook secret" value={manualWebhookSecret} onChange={(e) => setManualWebhookSecret(e.target.value)} disabled={manualSaving} autoComplete="off" />
              </Field>
              <Field label="Private key (PEM)">
                <Textarea
                  required
                  className="input--mono"
                  placeholder={'-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
                  value={manualPrivateKey}
                  onChange={(e) => setManualPrivateKey(e.target.value)}
                  disabled={manualSaving}
                  rows={5}
                />
              </Field>
              <ErrorNotice error={manualError} />
              <div className="integrations-form__actions">
                <button type="submit" className="button button--primary" disabled={manualSaving || manualIncomplete}>
                  {manualSaving ? 'Saving' : 'Connect GitHub App'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Dialog>
  );
}

// ── Repository access page ──────────────────────────────────────────────────

const providerCredential: Record<GitProvider, string> = {
  github: 'GitHub App',
  gitlab: 'Personal access token',
  bitbucket: 'App password',
};

const providerName: Record<GitProvider, string> = { github: 'GitHub', gitlab: 'GitLab', bitbucket: 'Bitbucket' };

export function IntegrationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [credentials, setCredentials] = useState<Record<GitProvider, SavedGitCredential | null>>({
    github: null,
    gitlab: null,
    bitbucket: null,
  });

  // GitHub App status
  const [githubAppStatus, setGithubAppStatus] = useState<GitHubAppStatusResponse | null>(null);
  const [githubAppLoading, setGithubAppLoading] = useState(true);
  const [githubSetupOpen, setGithubSetupOpen] = useState(false);
  const [githubRemoveOpen, setGithubRemoveOpen] = useState(false);
  const [githubRemoving, setGithubRemoving] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [exchangeError, setExchangeError] = useState<Error | null>(null);

  // Testing a token (GitLab / Bitbucket)
  const [testing, setTesting] = useState<Record<GitProvider, boolean>>({ github: false, gitlab: false, bitbucket: false });
  const [testNotice, setTestNotice] = useState<Record<GitProvider, string | null>>({ github: null, gitlab: null, bitbucket: null });
  const [testError, setTestError] = useState<Record<GitProvider, Error | null>>({ github: null, gitlab: null, bitbucket: null });

  // The repositories of one provider, under its row
  const [activeRepoProvider, setActiveRepoProvider] = useState<GitProvider | null>(null);
  const [reposLoading, setReposLoading] = useState(false);
  const [repos, setRepos] = useState<RemoteRepository[]>([]);
  const [repoSearch, setRepoSearch] = useState('');
  const [reposError, setReposError] = useState<Error | null>(null);
  const reposId = useId();
  const tokenInputId = useId();

  // Connect / update a token
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProvider, setModalProvider] = useState<TokenProvider>('gitlab');
  const [modalAccount, setModalAccount] = useState('');
  const [modalToken, setModalToken] = useState('');
  const [modalHost, setModalHost] = useState('');
  const [modalConnecting, setModalConnecting] = useState(false);
  const [modalError, setModalError] = useState<Error | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Forget a token
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnectProvider, setDisconnectProvider] = useState<TokenProvider | null>(null);

  function refreshCredentials() {
    setCredentials({
      github: getSavedGitCredential('github'),
      gitlab: getSavedGitCredential('gitlab'),
      bitbucket: getSavedGitCredential('bitbucket'),
    });
  }

  const loadGitHubAppStatus = useCallback(async () => {
    setGithubAppLoading(true);
    try {
      setGithubAppStatus(await api.getGitHubApp());
    } catch {
      setGithubAppStatus(null);
    } finally {
      setGithubAppLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCredentials();
    loadGitHubAppStatus();
    const handleUpdate = () => refreshCredentials();
    window.addEventListener('co_git_credentials_updated', handleUpdate);
    window.addEventListener('storage', handleUpdate);
    return () => {
      window.removeEventListener('co_git_credentials_updated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [loadGitHubAppStatus]);

  // GitHub sends the browser back here: ?code=… after registering the app
  // (manifest flow), ?installation_id=…&setup_action=install after installing it.
  useEffect(() => {
    const code = searchParams.get('code');
    const setupAction = searchParams.get('setup_action');
    const installationId = searchParams.get('installation_id');
    if (code && !setupAction) {
      setExchanging(true);
      setExchangeError(null);
      api
        .exchangeGitHubAppCode(code)
        .then(() => {
          const next = new URLSearchParams(searchParams);
          next.delete('code');
          setSearchParams(next, { replace: true });
          void loadGitHubAppStatus();
        })
        .catch((err) => setExchangeError(asError(err)))
        .finally(() => setExchanging(false));
    } else if (installationId && setupAction === 'install') {
      const id = parseInt(installationId, 10);
      if (!isNaN(id)) {
        api
          .saveGitHubAppInstallation(id, '')
          .then(() => {
            const next = new URLSearchParams(searchParams);
            next.delete('installation_id');
            next.delete('setup_action');
            setSearchParams(next, { replace: true });
            void loadGitHubAppStatus();
          })
          .catch(() => {
            // Best-effort; the status can be refreshed.
          });
      }
    }
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const app = githubAppStatus?.app;
  const githubConfigured = Boolean(githubAppStatus?.configured && app);
  const githubInstalled = githubAppStatus?.installed ?? false;
  const appName = app ? app.app_name || app.app_slug || `App #${app.app_id}` : '';

  function handleOpenConnect(provider: TokenProvider) {
    const cred = credentials[provider];
    setModalProvider(provider);
    setModalAccount(cred?.account || '');
    setModalToken(cred?.token || '');
    setModalHost(cred?.host || '');
    setModalError(null);
    setShowPassword(false);
    setModalOpen(true);
  }

  async function handleSaveConnection(e: FormEvent) {
    e.preventDefault();
    setModalConnecting(true);
    setModalError(null);
    const trimmedAccount = modalAccount.trim();
    const trimmedToken = modalToken.trim();
    const trimmedHost = modalHost.trim();
    try {
      // Checked against the provider before it is kept.
      const res = await api.listGitProviderRepositories({ provider: modalProvider, account: trimmedAccount, token: trimmedToken });
      saveGitCredential(modalProvider, { account: trimmedAccount, token: trimmedToken, host: trimmedHost || undefined, connectedAt: new Date().toISOString() });
      refreshCredentials();
      setModalOpen(false);
      if (activeRepoProvider === modalProvider) setRepos(res.repositories || []);
    } catch (cause) {
      setModalError(asError(cause));
    } finally {
      setModalConnecting(false);
    }
  }

  async function handleTestConnection(provider: TokenProvider) {
    const cred = credentials[provider];
    if (!cred) return;
    setTesting((prev) => ({ ...prev, [provider]: true }));
    setTestNotice((prev) => ({ ...prev, [provider]: null }));
    setTestError((prev) => ({ ...prev, [provider]: null }));
    const startTime = Date.now();
    try {
      const res = await api.listGitProviderRepositories({ provider, account: cred.account, token: cred.token });
      const latency = Date.now() - startTime;
      const count = res.repositories ? res.repositories.length : 0;
      setTestNotice((prev) => ({ ...prev, [provider]: `Access to ${count} ${count === 1 ? 'repository' : 'repositories'} (${latency} ms round trip).` }));
    } catch (cause) {
      setTestError((prev) => ({ ...prev, [provider]: asError(cause) }));
    } finally {
      setTesting((prev) => ({ ...prev, [provider]: false }));
    }
  }

  async function fetchRepos(provider: GitProvider) {
    setReposLoading(true);
    setReposError(null);
    try {
      if (provider === 'github') {
        const res = await api.listGitHubAppRepositories();
        setRepos(res.repositories || []);
      } else {
        const cred = credentials[provider];
        if (!cred) return;
        const res = await api.listGitProviderRepositories({ provider, account: cred.account, token: cred.token });
        setRepos(res.repositories || []);
      }
    } catch (cause) {
      setReposError(asError(cause));
    } finally {
      setReposLoading(false);
    }
  }

  function handleToggleRepos(provider: GitProvider) {
    if (activeRepoProvider === provider) {
      setActiveRepoProvider(null);
      return;
    }
    setActiveRepoProvider(provider);
    setRepos([]);
    setRepoSearch('');
    void fetchRepos(provider);
  }

  async function handleRemoveGitHubApp() {
    setGithubRemoving(true);
    try {
      await api.deleteGitHubApp();
      setGithubRemoveOpen(false);
      if (activeRepoProvider === 'github') setActiveRepoProvider(null);
      void loadGitHubAppStatus();
    } catch {
      // Best-effort; the status is read again either way.
    } finally {
      setGithubRemoving(false);
    }
  }

  function handleConfirmDisconnect() {
    if (disconnectProvider) {
      removeGitCredential(disconnectProvider);
      refreshCredentials();
      if (activeRepoProvider === disconnectProvider) {
        setActiveRepoProvider(null);
        setRepos([]);
      }
    }
    setDisconnectModalOpen(false);
    setDisconnectProvider(null);
  }

  const filteredRepos = useFilteredRepos(repos, repoSearch);
  const activeProviderMeta = PROVIDERS.find((p) => p.id === modalProvider) ?? PROVIDERS[0]!;
  const disconnectMeta = PROVIDERS.find((p) => p.id === disconnectProvider);
  const providers: GitProvider[] = ['github', 'gitlab', 'bitbucket'];

  const columns: Column<GitProvider>[] = [
    {
      id: 'provider',
      header: 'Provider',
      card: 'title',
      width: 'fill',
      cell: (provider) => (
        <span className="cell-stack">
          <ProviderTitle provider={provider}>{providerName[provider]}</ProviderTitle>
          <span className="cell-stack__secondary">{providerCredential[provider]}</span>
        </span>
      ),
    },
    {
      id: 'account',
      header: 'Connected account',
      cardLabel: 'Account',
      width: 24,
      cell: (provider) => {
        if (provider === 'github') {
          if (githubAppLoading && !githubAppStatus) return <span className="muted">Loading</span>;
          return githubConfigured && app ? (
            <span className="cell-stack">
              <span>{app.account || appName}</span>
              <span className="cell-stack__secondary">{appName}</span>
              <span className="cell-stack__secondary tabular-nums">
                App ID {app.app_id}
                {app.installation_id ? ` · installation ${app.installation_id}` : ''}
              </span>
            </span>
          ) : (
            <span className="muted">No account</span>
          );
        }
        const cred = credentials[provider];
        return cred ? (
          <span className="cell-stack">
            <span>{cred.account}</span>
            <span className="cell-stack__secondary">
              {cred.host || PROVIDERS.find((p) => p.id === provider)?.defaultHost} · <code>{cred.token.slice(0, 4)}••••••••••••</code>
            </span>
          </span>
        ) : (
          <span className="muted">No account</span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      card: 'status',
      // Wider than a usual status: "Not connected" stays on one line.
      width: 14,
      cell: (provider) => {
        if (provider === 'github') {
          if (!githubConfigured) return <StatusBadge kind="connection" value="not_connected" />;
          return githubInstalled ? <StatusBadge kind="connection" value="installed" /> : <StatusBadge kind="connection" value="awaiting_installation" />;
        }
        return credentials[provider] ? (
          <StatusBadge kind="connection" value="configured" label="Ready for this visit" title="Held in memory for this visit only; saved per project when a project is created" />
        ) : (
          <StatusBadge kind="connection" value="per_project" title="Stored per project when a project is created" />
        );
      },
    },
    {
      id: 'scope',
      header: 'Scope',
      width: 20,
      priority: 'secondary',
      cell: (provider) =>
        provider === 'github'
          ? 'The whole organization'
          : credentials[provider]
            ? 'This visit; each project keeps its own copy'
            : 'Each project, when it is created',
    },
  ];

  return (
    <div className="integrations-page">
      <Section
        title="Git providers"
        testId="integrations-providers"
      >
        {exchanging ? <Callout tone="info" title="Exchanging the code with GitHub…" /> : null}
        <ErrorNotice error={exchangeError} />
        {githubConfigured && app && !githubInstalled ? (
          <Callout
            tone="warning"
            title="Install the GitHub App"
            testId="integrations-github-install"
            actions={
              <a href={`https://github.com/apps/${app.app_slug}/installations/new`} target="_blank" rel="noreferrer" className="button button--secondary button--small">
                Install on GitHub <Icon name="external-link" size={16} />
              </a>
            }
          >
            <p>{appName} is registered but reads no repository until it is installed on a GitHub account or organization.</p>
          </Callout>
        ) : null}
        {(['gitlab', 'bitbucket'] as TokenProvider[]).map((provider) =>
          testNotice[provider] ? (
            <Callout key={provider} tone="success" title="Connection verified">
              <p>{testNotice[provider]}</p>
            </Callout>
          ) : null,
        )}
        {(['gitlab', 'bitbucket'] as TokenProvider[]).map((provider) => <ErrorNotice key={provider} error={testError[provider]} />)}

        <ResponsiveTable
          rows={providers}
          rowKey={(provider) => provider}
          caption="Git providers"
          className="integrations-table"
          columns={columns}
          actionsSize="lg"
          rowProps={(provider) => ({ testId: `integrations-${provider}` })}
          actions={(provider) => {
            if (provider === 'github') {
              if (!githubConfigured) {
                return (
                  <button type="button" className="button button--secondary button--small" onClick={() => setGithubSetupOpen(true)} disabled={githubAppLoading}>
                    Connect GitHub
                  </button>
                );
              }
              return (
                <>
                  {githubInstalled ? (
                    <button
                      type="button"
                      className="button button--secondary button--small"
                      aria-expanded={activeRepoProvider === 'github'}
                      aria-controls={activeRepoProvider === 'github' ? reposId : undefined}
                      onClick={() => handleToggleRepos('github')}
                    >
                      {activeRepoProvider === 'github' ? 'Hide repositories' : 'View repositories'}
                    </button>
                  ) : null}
                  <ActionMenu label="More actions for GitHub" size="sm" items={[{ label: 'Remove app', onClick: () => setGithubRemoveOpen(true), danger: true }]} />
                </>
              );
            }
            const cred = credentials[provider];
            if (!cred) {
              return (
                <button type="button" className="button button--secondary button--small" onClick={() => handleOpenConnect(provider)}>
                  Connect {providerName[provider]}
                </button>
              );
            }
            return (
              <>
                <button
                  type="button"
                  className="button button--secondary button--small"
                  aria-expanded={activeRepoProvider === provider}
                  aria-controls={activeRepoProvider === provider ? reposId : undefined}
                  onClick={() => handleToggleRepos(provider)}
                >
                  {activeRepoProvider === provider ? 'Hide repositories' : 'View repositories'}
                </button>
                <ActionMenu
                  label={`More actions for ${providerName[provider]}`}
                  size="sm"
                  items={[
                    { label: testing[provider] ? 'Testing' : 'Test connection', onClick: () => void handleTestConnection(provider), disabled: testing[provider] },
                    { label: 'Update token', onClick: () => handleOpenConnect(provider) },
                    {
                      label: 'Forget token',
                      danger: true,
                      onClick: () => {
                        setDisconnectProvider(provider);
                        setDisconnectModalOpen(true);
                      },
                    },
                  ]}
                />
              </>
            );
          }}
          detail={(provider) =>
            activeRepoProvider === provider ? (
              <RepositoryList
                id={reposId}
                label={`${providerName[provider]} repositories`}
                repos={repos}
                filtered={filteredRepos}
                loading={reposLoading}
                error={reposError}
                search={repoSearch}
                onSearch={setRepoSearch}
                onRefresh={() => void fetchRepos(provider)}
                emptyDescription={
                  provider === 'github'
                    ? "No repositories. Choose repositories in the app's settings on GitHub."
                    : 'No repositories'
                }
              />
            ) : null
          }
        />
      </Section>

      <GitHubSetupDialog
        open={githubSetupOpen}
        onClose={() => setGithubSetupOpen(false)}
        onConnected={() => {
          setGithubSetupOpen(false);
          void loadGitHubAppStatus();
        }}
      />

      <ConfirmDialog
        open={githubRemoveOpen}
        title="Remove GitHub App?"
        description={`Commerce Ops forgets the GitHub App configuration (${appName}). The app itself stays registered on GitHub; you can uninstall it from your GitHub settings.`}
        confirmLabel="Remove app"
        danger
        busy={githubRemoving}
        onConfirm={() => void handleRemoveGitHubApp()}
        onCancel={() => setGithubRemoveOpen(false)}
      />

      {/* Connect or update a GitLab or Bitbucket token */}
      <Dialog
        open={modalOpen}
        size="md"
        title={<ProviderTitle provider={modalProvider}>Connect {activeProviderMeta.name}</ProviderTitle>}
        onClose={() => setModalOpen(false)}
        busy={modalConnecting}
        onSubmit={handleSaveConnection}
        testId="integrations-connect-dialog"
        footer={
          <>
            <button type="button" className="button button--secondary" onClick={() => setModalOpen(false)} disabled={modalConnecting}>
              Cancel
            </button>
            <button type="submit" className="button button--primary" disabled={modalConnecting || !modalAccount.trim() || !modalToken.trim()}>
              {modalConnecting ? 'Verifying access' : 'Verify and save'}
            </button>
          </>
        }
      >
        <Callout tone="info" title="Required token permissions">
          <p>{activeProviderMeta.scopesNote}</p>
          <p>
            <a href={activeProviderMeta.tokenDocUrl} target="_blank" rel="noreferrer">
              Create a token on {activeProviderMeta.name} <Icon name="external-link" size={16} />
            </a>
          </p>
        </Callout>

        <Field label="Account or organization name">
          <Input
            type="text"
            required
            placeholder={modalProvider === 'bitbucket' ? 'workspace-name' : 'username-or-org'}
            spellCheck={false}
            autoComplete="off"
            value={modalAccount}
            onChange={(e) => setModalAccount(e.target.value)}
            disabled={modalConnecting}
          />
        </Field>

        {/* Not a <Field>: the Show/Hide toggle sits in the label row and must
            not become part of the input's accessible name. */}
        <div className="field">
          <div className="integrations-secret__label">
            <label className="field__label" htmlFor={tokenInputId}>
              {activeProviderMeta.tokenType}
            </label>
            <button type="button" className="button button--ghost button--small" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide token' : 'Show token'}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <Input
            id={tokenInputId}
            type={showPassword ? 'text' : 'password'}
            required
            className={showPassword ? undefined : 'input--mono'}
            placeholder={activeProviderMeta.tokenPlaceholder}
            value={modalToken}
            onChange={(e) => setModalToken(e.target.value)}
            disabled={modalConnecting}
            autoComplete="off"
          />
        </div>

        {modalProvider === 'gitlab' && (
          <Field label="Custom host" hint="Optional, for a self-hosted GitLab.">
            <Input type="text" placeholder="e.g. gitlab.mycompany.internal" spellCheck={false} autoComplete="off" value={modalHost} onChange={(e) => setModalHost(e.target.value)} disabled={modalConnecting} />
          </Field>
        )}

        <ErrorNotice error={modalError} />
      </Dialog>

      <ConfirmDialog
        open={disconnectModalOpen && !!disconnectProvider}
        title={`Forget the ${disconnectMeta?.name ?? ''} token?`}
        description={`The token for ${disconnectProvider ? credentials[disconnectProvider]?.account ?? '' : ''} is forgotten for this visit. Projects that already store their own copy of it keep working; nothing is revoked at ${disconnectMeta?.name ?? 'the provider'}.`}
        confirmLabel="Forget token"
        danger
        onConfirm={handleConfirmDisconnect}
        onCancel={() => {
          setDisconnectModalOpen(false);
          setDisconnectProvider(null);
        }}
      />
    </div>
  );
}
